import { cn } from "@/lib/utils";
import { SiInstagram, SiThreads } from "react-icons/si";
import { FaLinkedin } from "react-icons/fa";
import { lazy, Suspense, useState, type MouseEvent } from "react";
import { useTranslation } from "@/hooks/useTranslation";
import { withCurrentYear } from "@shared/copyright";

// Fetched on the first click, not with every sign-in page.
const PrivacyPolicyDialog = lazy(() => import("@/components/PrivacyPolicyDialog").then((m) => ({ default: m.PrivacyPolicyDialog })));
const TermsOfUseDialog = lazy(() => import("@/components/TermsOfUseDialog").then((m) => ({ default: m.TermsOfUseDialog })));

/**
 * PropPay's MarketingFooter, in SaleFish's words (2026-09-27): one centred
 * column under a hairline — the legal links, a rule, the socials — and the
 * copyright line beneath. It was a three-part row that split left/centre/right
 * on a desk and restacked in a different order on a phone.
 *
 * The footer is the bottom-most element on every page that renders it, so it
 * owns the home-indicator inset. Side gutters come from --gutter; the landscape
 * notch is already handled by the enclosing .auth-shell. `bg-background`
 * because the pages it closes paint no surface of their own under it.
 */
export function Footer({ className }: {
  /**
   * Merged last. The sign-in screens pass `dark bg-transparent`: the footer
   * sits on the sign-in film there, in both themes (AuthPageShell). They used
   * to pass `followContent`, which let the footer follow the card on a phone
   * because the bottom of a 100dvh page is BEHIND iOS 26 Safari's floating
   * toolbar (2026-09-28); that shell is now 100svh below md instead, so the
   * footer can sit on the bottom edge and still clear the toolbar.
   */
  className?: string
} = {}) {
  const t = useTranslation();
  /* The legal texts open IN PLACE, as dialogs (Andrew, 2026-09-28: "the
     Privacy and Terms should open in dialogs") — from a half-filled sign-in or
     registration form, a link away would lose what was typed. They stay real
     links to /privacy-policy and /terms-of-use, so a new-tab click, a crawler
     and a reader with no script still reach the text; only a plain click is
     taken over. */
  const [legal, setLegal] = useState<null | "privacy" | "terms">(null);
  const openInPlace = (which: "privacy" | "terms") => (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    setLegal(which);
  };
  const close = (open: boolean) => {
    if (!open) setLegal(null);
  };
  /* The legal links are wider than 44px already, so `hit-target` grows them
     vertically only, from their ink to a 44px target, without adding footer
     height. */
  const linkCls =
    "hit-target whitespace-nowrap rounded-sm px-2.5 py-1 text-foreground/70 transition-colors hover:bg-foreground/[.05] hover:text-primary-ink";
  const socialCls =
    "grid size-8 place-items-center rounded-full text-foreground/65 transition-colors hover:bg-foreground/[.05] hover:text-primary-ink coarse:size-11";
  return (
    <footer
      className={cn("mt-auto shrink-0 bg-background pt-4 sm:pt-8", className)}
      style={{
        paddingBottom: "calc(0.875rem + var(--sa-bottom))",
        paddingLeft: "var(--gutter)",
        paddingRight: "var(--gutter)",
      }}
    >
      <div className="mx-auto max-w-[1100px] border-t border-border pt-3 sm:pb-6 sm:pt-7">
        <div className="flex flex-col items-center gap-2 sm:gap-3.5">
          <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
            <nav aria-label={t("shellLegalNavLabel", "Legal")} className="flex items-center gap-1 text-label font-semibold">
              <a href="/privacy-policy" onClick={openInPlace("privacy")} aria-haspopup="dialog" className={linkCls} data-testid="link-privacy-policy">
                {t("shellTitlePrivacyPolicy", "Privacy Policy")}
              </a>
              <span className="h-3.5 w-px shrink-0 bg-border" aria-hidden="true" />
              <a href="/terms-of-use" onClick={openInPlace("terms")} aria-haspopup="dialog" className={linkCls} data-testid="link-terms-of-service">
                {t("shellLegalTermsTitle", "Terms of Service")}
              </a>
            </nav>
            <span className="mx-1 hidden h-3.5 w-px shrink-0 bg-border sm:block" aria-hidden="true" />
            {/* 32px, as PropPay's are, with a mouse; 44 under a finger, which
                the layout audit holds every touch target to. Three at 44 still
                share the legal links' line on a 402px phone. */}
            <div className="flex shrink-0 items-center gap-1">
              <a
                href="https://www.linkedin.com/company/salefishapp/"
                target="_blank"
                rel="noopener noreferrer"
                className={socialCls}
                data-testid="link-linkedin"
                aria-label={t("shellLegalOpensInNewTab", "{name} (Opens in New Tab)", { name: "LinkedIn" })}
              >
                <FaLinkedin className="size-[18px]" aria-hidden="true" />
              </a>
              <a
                href="https://www.instagram.com/salefishapp"
                target="_blank"
                rel="noopener noreferrer"
                className={socialCls}
                data-testid="link-instagram"
                aria-label={t("shellLegalOpensInNewTab", "{name} (Opens in New Tab)", { name: "Instagram" })}
              >
                <SiInstagram className="size-[18px]" aria-hidden="true" />
              </a>
              <a
                href="https://www.threads.com/@salefishapp"
                target="_blank"
                rel="noopener noreferrer"
                className={socialCls}
                data-testid="link-threads"
                aria-label={t("shellLegalOpensInNewTab", "{name} (Opens in New Tab)", { name: "Threads" })}
              >
                <SiThreads className="size-[18px]" aria-hidden="true" />
              </a>
            </div>
          </div>
          <p className="text-center text-label leading-relaxed text-muted-foreground">
            {/* The year is never stored — not in this file and not in the
                translation row this prefers. `withCurrentYear` substitutes
                `{year}`, and rewrites a hardcoded one for the rows in
                translations_registry that predate the placeholder. */}
            {withCurrentYear(t("copyright", "© {year} SaleFish Inc. All rights reserved.") || "© {year} SaleFish Inc. All rights reserved.")}
            <br className="sm:hidden" />
            <span className="hidden sm:inline"> · </span>
            {/* Words, not a heart glyph: an icon here is decoration. */}
            {t("shellLegalMadeWith", "Made with")} {t("shellLegalLove", "love")}{" "}
            {t("shellLegalMadeIn", "in Toronto, Calgary & Vancouver")}
          </p>
        </div>
      </div>
      {/* Mounted only while open, so the lazy chunk is fetched on demand. */}
      {legal && (
        <Suspense fallback={null}>
          {legal === "privacy" ? <PrivacyPolicyDialog defaultOpen onOpenChange={close} /> : <TermsOfUseDialog defaultOpen onOpenChange={close} />}
        </Suspense>
      )}
    </footer>
  );
}
